import React, { useEffect, useMemo, useState } from 'react';
import { FiPlus, FiTrash2, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { api } from '../utils/api';
import toast from 'react-hot-toast';

type Assistant = { id: string; name: string; voiceEnabled?: boolean };
type ActiveTag = { id: string; name: string; tag: string; linkedAssistantId?: string | null; updatedAt: string; createdAt: string; linkedAssistant?: Assistant | null };

const TagsPage: React.FC = () => {
  const [tags, setTags] = useState<ActiveTag[]>([]);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<{ id?: string; name: string; tag: string; linkedAssistantId?: string | null }>({ name: '', tag: '', linkedAssistantId: null });
  const total = tags.length;

  useEffect(() => {
    const load = async () => {
      try {
        const [tagRes, asstRes] = await Promise.all([
          api.get('/knowledge/tags'),
          api.get('/assistants')
        ]);
        setTags(tagRes.data?.tags || []);
        setAssistants((asstRes.data?.assistants || []).map((a: any) => ({ id: a.id, name: a.name, voiceEnabled: a.voiceEnabled })));
      } catch (e) {
        console.error('Failed to load tags', e);
      }
    };
    load();
  }, []);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return tags.slice(start, start + pageSize);
  }, [tags, page]);

  const openCreate = () => {
    setForm({ name: '', tag: '', linkedAssistantId: null });
    setModalOpen(true);
  };

  const openEdit = (t: ActiveTag) => {
    setForm({ id: t.id, name: t.name, tag: t.tag, linkedAssistantId: t.linkedAssistantId || null });
    setModalOpen(true);
  };

  const save = async () => {
    try {
      if (!form.name || !form.tag) {
        toast.error('Name and Active Tag are required');
        return;
      }
      if (form.id) {
        const rsp = await api.put(`/knowledge/tags/${form.id}`, { name: form.name, tag: form.tag, linkedAssistantId: form.linkedAssistantId || undefined });
        setTags(prev => prev.map(t => t.id === form.id ? rsp.data.tag : t));
      } else {
        const rsp = await api.post('/knowledge/tags', { name: form.name, tag: form.tag, linkedAssistantId: form.linkedAssistantId || undefined });
        setTags(prev => [rsp.data.tag, ...prev]);
      }
      setModalOpen(false);
      toast.success('Saved');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to save');
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this tag?')) return;
    await api.delete(`/knowledge/tags/${id}`);
    setTags(prev => prev.filter(t => t.id !== id));
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>Tags</h1>
        <button onClick={openCreate} className="px-4 py-2 rounded" style={{ background: 'var(--color-primary)', color: '#fff' }}>
          <span className="inline-flex items-center gap-2"><FiPlus /> Create Active Tag</span>
        </button>
      </div>

      <div className="rounded-lg border" style={{ borderColor: 'var(--color-border-primary)', background: 'var(--color-bg-secondary)' }}>
        <div className="grid" style={{ gridTemplateColumns: 'minmax(240px,1fr) 180px 240px 280px 80px', padding: '12px 16px', color: 'var(--color-text-secondary)' }}>
          <div>NAME</div>
          <div>UPDATED</div>
          <div>TAG</div>
          <div>LINKED ASSISTANT</div>
          <div></div>
        </div>
        <div>
          {paged.map(t => (
            <div key={t.id} className="grid items-center" style={{ gridTemplateColumns: 'minmax(240px,1fr) 180px 240px 280px 80px', padding: '12px 16px', borderTop: '1px solid var(--color-border-primary)' }}>
              <button className="text-left" onClick={() => openEdit(t)} style={{ color: 'var(--color-text-primary)' }}>{t.name}</button>
              <div style={{ color: 'var(--color-text-secondary)' }}>{new Date(t.updatedAt).toLocaleString()}</div>
              <div style={{ color: 'var(--color-text-secondary)' }}>{t.tag}</div>
              <div>
                {(() => {
                  const linked = !!t.linkedAssistant;
                  const active = linked && t.linkedAssistant!.voiceEnabled;
                  const badgeClass = linked
                    ? active
                      ? 'bg-green-600/20 text-green-400'
                      : 'bg-yellow-600/20 text-yellow-400'
                    : 'bg-gray-600/20 text-gray-400';
                  const label = linked ? (active ? 'ACTIVE' : 'ARCHIVED') : '—';
                  return (
                    <span className={`px-2 py-1 rounded text-[11px] font-semibold tracking-wide uppercase mr-2 ${badgeClass}`}>{label}</span>
                  );
                })()}
                <span style={{ color: 'var(--color-text-primary)' }}>{t.linkedAssistant?.name || 'Unlinked'}</span>
              </div>
              <div className="flex justify-end">
                <button onClick={() => remove(t.id)} className="p-2 rounded hover:opacity-80" title="Delete" style={{ color: 'var(--color-text-secondary)' }}>
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mt-3" style={{ color: 'var(--color-text-secondary)' }}>
        <div>Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total} Results</div>
        <div className="flex items-center gap-2">
          <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="p-2 rounded border" style={{ borderColor: 'var(--color-border-primary)' }}><FiChevronLeft /></button>
          <button disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)} className="p-2 rounded border" style={{ borderColor: 'var(--color-border-primary)' }}><FiChevronRight /></button>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 flex items-center justify-center" onClick={() => setModalOpen(false)}>
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)' }} />
          <div className="relative z-10 w-full max-w-lg rounded-lg p-5" onClick={e => e.stopPropagation()} style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}>
            <h2 className="text-lg font-semibold mb-3">Active tag</h2>
            <div className="space-y-3">
              <label className="block text-sm">Name
                <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="w-full mt-1 px-3 py-2 rounded border" style={{ background: 'transparent', borderColor: 'var(--color-border-primary)', color: 'var(--color-text-primary)' }} />
              </label>
              <label className="block text-sm">Linked assistant
                <select
                  value={form.linkedAssistantId ?? ''}
                  onChange={(e) => setForm(f => ({ ...f, linkedAssistantId: e.target.value || null }))}
                  className="w-full mt-1 px-3 py-2 rounded border focus:outline-none"
                  style={{ background: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                >
                  <option value="" disabled hidden>Select assistant</option>
                  {assistants.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name}{a.voiceEnabled ? '' : ' (archived)'}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">Active Tag
                <input value={form.tag} onChange={(e) => setForm(f => ({ ...f, tag: e.target.value }))} className="w-full mt-1 px-3 py-2 rounded border" placeholder="cl_booking_assistant" style={{ background: 'transparent', borderColor: 'var(--color-border-primary)', color: 'var(--color-text-primary)' }} />
              </label>
            </div>
            <div className="flex justify-end mt-4">
              {(!form.name || !form.tag) && (
                <p className="text-xs text-red-400 mt-2">Name and Tag are required.</p>
              )}
              <button
                disabled={!form.name || !form.tag}
                onClick={save}
                className="px-4 py-2 rounded disabled:opacity-50"
                style={{ background: 'var(--color-primary)', color: '#fff' }}
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsPage;


